<template>
  <!-- Top title bar -->

  <div v-if="isLoading" class="p-4 text-center">
    <span class="loading loading-spinner loading-lg text-base-content/60"></span>
  </div>
  <div v-else class="h-full flex flex-col">
    <header class="sticky top-0 z-50 bg-base-100">
      <div class="navbar px-4 flex items-center min-h-0">
        <div class="flex-1">
          <h1 class="text-xl font-bold">
            Kelionė
            <span class="text-sm font-normal text-base-content/60">{{
              travelInfo.date
            }}</span>
          </h1>
        </div>
        <div v-if="travelInfo.status" class="flex items-center my-auto">
          <span :class="['badge', statusConfig[travelInfo.status].class]">
            {{ statusConfig[travelInfo.status].text }}
          </span>
        </div>
        <div class="flex-none"></div>
      </div>
    </header>
    <!-- Map (takes remaining space) -->
    <div class="flex-1">
      <Map
        :waypoints-geometry="travelInfo.waypoints"
        :route-geometry="routeGeometry"
        :center="[21.39, 55.71]"
        :zoom="10"
        height="100%"
        :layerControl="false"
        :mapControl="false"
        :thematicLayers="false"
        :userLocation="trackingStore.lastReportedLocation"
        class="w-full h-full"
      />
    </div>

    <!-- Handle section -->
    <div class="bg-base-100 rounded-t-lg shadow-[0_0_15px_rgba(0,0,0,0.1)]">
      <div
        @click="toggleTimeline"
        class="flex justify-center items-center cursor-pointer rounded-t-lg py-3"
      >
        <div class="w-12 h-2 bg-base-300 rounded-full"></div>
      </div>

      <!-- Timeline panel -->
      <div
        class="transform transition-transform duration-300 max-h-[60vh] overflow-y-auto"
        :class="{ hidden: !isTimelineExpanded }"
      >
        <div class="px-4">
          <Timeline
            :waypoints="travelInfo.waypoints"
            @selectTrip="handleTripSelection"
          />
        </div>
      </div>
    </div>

    <!-- Info section -->
    <div class="bg-base-100 pt-2">
      <div
        v-if="
          travelInfo.date_status === 'today' &&
          travelInfo.status === 'travelStatus1'
        "
        class="px-4 pt-2 pb-4"
      >
        <button
          @click="handleTravelStart"
          class="btn btn-success w-full rounded-xl h-14"
        >
          Pradėti kelionę
        </button>
      </div>
      <div
        v-if="
          nextWaypoint &&
          travelInfo.status !== 'travelStatus1' &&
          nextWaypoint.type !== 'finish'
        "
        class="px-4 pb-3"
      >
        <div class="text-sm text-base-content/60">Kitas sustojimas:</div>
        <div class="text-sm" v-if="currentTrip?.clients">
          <span v-for="(client, index) in currentTrip.clients" :key="client.id">
            {{ client.name }} {{ client.surname
            }}<span v-if="index < currentTrip.clients.length - 1">, </span>
          </span>
        </div>
        <div class="font-medium mb-3">
          {{ nextWaypoint.time }} -> {{ nextWaypoint.address }}
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="handleWaypointStart(nextWaypoint.id)"
            class="btn btn-success"
          >
            Patvirtinti
          </button>
          <button
            @click="openPassengerNotArrivedModal(nextWaypoint.trip_id)"
            class="btn"
          >
            Neatvyko
          </button>
          <button @click="openPassengerModal(nextWaypoint.trip_id)" class="btn">
            <DefaultIcon name="info" size="w-6 h-6" />
          </button>
          <button
            @click="openRouteFromCurrentLocation(nextWaypoint)"
            class="btn"
          >
            <DefaultIcon name="car" size="w-6 h-6" />
          </button>
        </div>
      </div>
      <div
        v-if="
          nextWaypoint.type == 'finish' && travelInfo.status == 'travelStatus2'
        "
        class="px-4 pt-2 pb-4"
      >
        <button
          @click="handleTravelFinish"
          class="btn btn-success w-full h-14"
        >
          Baigti kelionę
        </button>
      </div>
    </div>
  </div>

  <!-- Modal component -->
  <PassengerInfoModal
    :is-open="isPassengerModalOpen"
    :trip-id="selectedTripId"
    @close="closePassengerModal"
    @updated="handleTripUpdated"
  />

  <!-- Modal component -->
  <PassengerNotArrivedModal
    :is-open="isPassengerNotArrivedModalOpen"
    :trip-id="selectedTripId"
    @close="closePassengerNotArrivedModal"
    @updated="handleTripUpdated"
  />

  <Toast ref="toast" class="z-50" />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useTravelStore } from "@/stores/travels";
import { useWaypointStore } from "@/stores/waypoints";
import { useTrackingStore } from "@/stores/tracking";
import { useRouter, useRoute } from "vue-router";

import { useRouteCalculation } from "@/composables/routing/useRouteCalculation";
import { useGeolocation } from "@/composables/geolocation/useGeolocation";
import Map from "@/components/maps/MapComponent.vue";
import Timeline from "@/components/timelines/DriverTimeline.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Toast from "@/components/ui/alerts/ToastComponent.vue";
import PassengerInfoModal from "@/components/ui/modals/PassengerInfoModal.vue";
import PassengerNotArrivedModal from "@/components/ui/modals/PassengerNotArrivedModal.vue";

const travelStore = useTravelStore();
const waypointStore = useWaypointStore();
const trackingStore = useTrackingStore();

const { calculateRoute } = useRouteCalculation();
const geolocation = useGeolocation();

const router = useRouter();
const route = useRoute();
const travelId = route.params.id;
const driverId = 8;

const isLoading = ref(true);
const travelInfo = ref(null);
const toast = ref(null);

const statusConfig = {
  travelStatus1: {
    text: "Planuojama",
    class: "badge-info",
  },
  travelStatus2: {
    text: "Vykdoma",
    class: "badge-warning",
  },
  travelStatus3: {
    text: "Pasibaigusi",
    class: "badge-success",
  },
};

/*
  Load travel
*/

const loadTravelData = async () => {
  try {
    isLoading.value = true;
    const response = await travelStore.fetchTravelById(travelId);
    travelInfo.value = response;
    await updateTravelRoute();
  } catch (error) {
    console.error("Error fetching trip data:", error);
    // Handle error appropriately
  } finally {
    isLoading.value = false;
  }
};

// Computed property to find the first waypoint with time_fact = null and type ≠ "start"
const nextWaypoint = computed(() => {
  if (!travelInfo.value || !travelInfo.value.waypoints) return null;

  return travelInfo.value.waypoints.find(
    (waypoint) =>
      (waypoint.time_fact === null && waypoint.type !== "start") ||
      waypoint.type == "finish",
  );
});

const currentTrip = computed(() => {
  if (!nextWaypoint.value || !travelInfo.value?.trips) return null;

  return travelInfo.value.trips.find(
    (trip) => trip.id === nextWaypoint.value.trip_id,
  );
});

/*
  Calculate route
*/

const routeGeometry = ref(null);

const updateTravelRoute = async () => {
  try {
    if (travelInfo.value.waypoints.length > 1) {
      // Calc route
      // Calculate route if waypoints exist
      if (travelInfo.value.waypoints?.length > 1) {
        const result = await calculateRoute({
          waypoints: travelInfo.value.waypoints,
          travelPoint:
            travelInfo.value.travelLat && travelInfo.value.travelLng
              ? {
                  lat: travelInfo.value.travelLat,
                  lng: travelInfo.value.travelLng,
                }
              : null,
        });

        // Update trip info with the calculated route
        travelInfo.value.tripRoute = result.summary;
        routeGeometry.value = result.geometry;
      }
    } else {
      routeGeometry.value = null;
      travelInfo.value.tripRoute = [];
    }
  } catch (error) {
    console.error("Error updating trip waypoints:", error);
    // Handle error appropriately - maybe emit an event or show a notification
  }
};

const updateRouteWithTracking = async () => {
  // Extract current location from trackingStore and next waypoint from travelInfo
  const currentLocation = trackingStore.lastReportedLocation;
  const nextWp = nextWaypoint.value;

  // Ensure both values are available
  if (!currentLocation || !nextWp) return;

  // Create an array of waypoints starting with current location and ending with next waypoint.
  // You might add more points in between if needed.
  const trackingWaypoints = [
    {
      lat: currentLocation.lat,
      lng: currentLocation.lng,
    },
    {
      lat: parseFloat(nextWp.lat),
      lng: parseFloat(nextWp.lng),
    },
  ];

  try {
    const result = await calculateRoute({
      // Pass the tracking-specific waypoints array
      waypoints: trackingWaypoints,
    });

    // Update the travel info with the new route data
    routeGeometry.value = result.geometry;
  } catch (error) {
    console.error("Error updating route from tracking:", error);
  }
};

watch(
  [() => trackingStore.lastReportedLocation, () => nextWaypoint.value],
  ([newLocation, newNextWaypoint]) => {
    if (newLocation && newNextWaypoint) {
      updateRouteWithTracking();
    }
  },
  { immediate: true },
);

/*
  Manage travel
*/

const handleTravelStart = async () => {
  try {
    await travelStore.startTravel(travelInfo.value.id);
    // Refresh the trips list after cancellation
    travelInfo.value = await travelStore.fetchTravelById(travelInfo.value.id);

    // Start tracking
    startTracking();
    toast.value.success("Kelionė pradėta", 3000, "toast-top");
  } catch (error) {
    console.error("Failed to complete trip:", error);
  }
};

const handleTravelFinish = async () => {
  try {
    await travelStore.finishTravel(travelInfo.value.id);

    // Refresh the trips list after cancellation
    travelInfo.value = await travelStore.fetchTravelById(travelInfo.value.id);

    // Stop tracking
    stopTracking();

    toast.value.success("Kelionė užbaigta", 3000, "toast-top");
    setTimeout(() => {
      router.push("/driving/travels");
    }, 3000);
  } catch (error) {
    console.error("Failed to finish trip:", error);
  }
};

/*
  Manage waypoints
*/

const handleWaypointStart = async (id) => {
  try {
    await waypointStore.startWaypoint(id);
    // Refresh the trips list after cancellation
    travelInfo.value = await travelStore.fetchTravelById(travelInfo.value.id);
    toast.value.success(
      "Atnaujinta. Važiuokite į kitą sustojimą",
      3000,
      "toast-top",
    );
  } catch (error) {
    console.error("Failed to start waypoint:", error);
  }
};

/*
  Manage passengers
*/

// Modal state
const isPassengerModalOpen = ref(false);
const isPassengerNotArrivedModalOpen = ref(false);
const selectedTripId = ref(null);

// Function to open the passenger modal
const openPassengerModal = (tripId) => {
  selectedTripId.value = tripId;
  isPassengerModalOpen.value = true;
};

const closePassengerModal = () => {
  isPassengerModalOpen.value = false;
  selectedTripId.value = null;
};

// Function to open the passenger modal
const openPassengerNotArrivedModal = (tripId) => {
  selectedTripId.value = tripId;
  isPassengerNotArrivedModalOpen.value = true;
};

const closePassengerNotArrivedModal = () => {
  isPassengerNotArrivedModalOpen.value = false;
  selectedTripId.value = null;
};

const handleTripUpdated = async () => {
  travelInfo.value = await travelStore.fetchTravelById(travelInfo.value.id);
  toast.value.success(
    "Atnaujinta. Važiuokite į kitą sustojimą",
    3000,
    "toast-top",
  );
};

const handleTripSelection = (tripId) => {
  openPassengerModal(tripId);
};

/*
  Manage tracking
*/

// Helper functions for tracking
function startTracking() {
  trackingStore.startTravel({
    newTravelId: travelId,
    newDriverId: driverId,
    geolocationHook: geolocation,
  });
}

function stopTracking() {
  trackingStore.stopTravel();
}

// Lifecycle hooks for handling page navigation
onMounted(async () => {
  await loadTravelData();

  // Resume tracking if travel is in progress but tracking isn't active
  if (
    travelInfo.value?.status === "travelStatus2" &&
    !trackingStore.isTracking
  ) {
    startTracking();
  }
});

onBeforeUnmount(() => {
  // Clean up store interval when leaving the page
  if (trackingStore.isTracking) {
    stopTracking();
  }
});

/**
 * Opens Google Maps with the route and instructions for the user
 */
const openRouteFromCurrentLocation = (nextWaypoint) => {
  if (!travelInfo.value?.waypoints?.length) return;

  // Base URL
  const baseUrl = "https://www.google.com/maps/dir/?api=1";

  // Use "current location" as origin
  const originParam = "current+location";

  // Form destination from nextWaypoint coordinates
  const destinationParam = `${nextWaypoint.lat},${nextWaypoint.lng}`;

  // Construct the complete URL
  let googleMapsUrl = `${baseUrl}&origin=${originParam}&destination=${destinationParam}`;

  // Add travel mode parameter
  googleMapsUrl += "&travelmode=driving";

  // Open in a new tab
  window.open(googleMapsUrl, "_blank");
};

// Style
const isTimelineExpanded = ref(false);
const toggleTimeline = () => {
  isTimelineExpanded.value = !isTimelineExpanded.value;
};
</script>

<style scoped></style>
